'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Paragraph } from '../ui/paragraph';
import { Field } from '../form/field';
import Link from '@eliadmin/core/src/components/link';

import { getDefaultUrlForTosOrGdprLocalized } from '../../utils/misc-utils';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    custom: z.object({
      operation_name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      operation_city: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      operation_type: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      operation_size: z.string(),
      estimated_time: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function PartnerContactForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      custom: {
        operation_name: '',
        operation_city: '',
        operation_type: '',
        operation_size: '',
        estimated_time: '',
      },
      email: '',
      phone: '',
      gdpr: true,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 sm:gap-8 lg:gap-12',
          'ea-partner-contact-form',
        )}
      >
        <div
          className={cn(
            'grid w-full grid-cols-12 gap-x-2 gap-y-6 sm:gap-x-4 lg:gap-x-6',
            'ea-partner-contact-form__fields-wrapper',
          )}
        >
          <Field
            required
            bottomBorder
            control={form.control}
            name="custom.operation_name"
            placeholder={`${t('text3554')} *`}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <Field
            required
            bottomBorder
            control={form.control}
            name="custom.operation_city"
            placeholder={`${t('text3555')} *`}
            className={{ wrapper: 'col-span-6 w-full' }}
          />
          <Field
            required
            bottomBorder
            control={form.control}
            name="custom.operation_type"
            placeholder={`${t('text3556')} *`}
            className={{ wrapper: 'col-span-6 w-full' }}
          />
          <Field
            bottomBorder
            control={form.control}
            name="custom.operation_size"
            placeholder={`${t('text3557')}`}
            className={{ wrapper: 'col-span-6 w-full' }}
          />
          <Field
            required
            bottomBorder
            control={form.control}
            name="custom.estimated_time"
            placeholder={`${t('text3558')} *`}
            className={{ wrapper: 'col-span-6 w-full' }}
          />
          <Field
            required
            bottomBorder
            control={form.control}
            name="email"
            type="email"
            placeholder="E-mail *"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            bottomBorder
            control={form.control}
            name="phone"
            placeholder={`${t('text3559')}`}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 sm:flex-row sm:items-center sm:justify-between">
          <Paragraph size="sm" className="w-full text-left">
            {t('text2341')}&nbsp;
            <Link
              prefetch={false}
              href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.(), locale, 'gdpr')}
              target="_blank"
              className="underline transition-all hover:opacity-80"
            >
              {t('text2342')}
            </Link>
            &nbsp;{t('text3354')}
          </Paragraph>
          <Button
            type="submit"
            className={cn('w-full whitespace-nowrap sm:w-fit', 'ea-contact-form__submit')}
            onClick={() => console.log(form.formState.errors)}
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')}
          </Button>
        </div>
        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12 hidden')}>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
